import { useState, useEffect, useCallback, useMemo } from "react";
import { usePermissions } from "@/context/PermissionContext";
import { getFinancialStats, upsertFinancialYear, addFinancialYear, deleteFinancialYear, getFinancialConfig, updateFinancialConfig } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Save, Trash2, Lock, Euro, Target } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList,
} from "recharts";

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const eur = (v) => new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const eur2 = (v) => new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(v || 0);
const compact = (v) => new Intl.NumberFormat("fr-BE", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0);

// green (low) -> yellow -> red (high) heatmap within a column
const heat = (v, min, max) => {
  if (!v || max === min) return "transparent";
  const t = Math.max(0, Math.min(1, (v - min) / (max - min)));
  const hue = 120 - t * 120; // 120=green .. 0=red
  return `hsl(${hue}, 72%, 86%)`;
};

export default function FinancialStatisticsPage() {
  const { isMasterAdmin, isExposition, loading: permLoading } = usePermissions();
  const canEdit = isMasterAdmin; // exposition can view only
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ years: [] });
  const [draft, setDraft] = useState({}); // { year: { jan: "..", .. } }
  const [dirty, setDirty] = useState({}); // { year: true }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newYear, setNewYear] = useState(String(new Date().getFullYear()));
  const [deleteYear, setDeleteYear] = useState(null);
  const [goal, setGoal] = useState(6);
  const [goalDraft, setGoalDraft] = useState("6");
  const [savingGoal, setSavingGoal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFinancialStats();
      const r = res.data.rows || [];
      setRows(r);
      setStats(res.data.statistics || { years: [] });
      const g = res.data.goal_pct ?? 6;
      setGoal(g);
      setGoalDraft(String(g));
      const d = {};
      r.forEach((row) => {
        d[row.year] = {};
        MONTHS.forEach((m) => { d[row.year][m] = row.months[m] ? String(row.months[m]) : ""; });
      });
      setDraft(d);
      setDirty({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const years = useMemo(() => Object.keys(draft).map(Number).sort((a, b) => b - a), [draft]);

  // per-column min/max for heatmap (computed from draft, excludes zeros)
  const colRange = useMemo(() => {
    const ranges = {};
    [...MONTHS, "total"].forEach((col) => {
      const vals = years.map((y) => {
        if (col === "total") return MONTHS.reduce((s, m) => s + (parseFloat(draft[y]?.[m]) || 0), 0);
        return parseFloat(draft[y]?.[col]) || 0;
      }).filter((v) => v > 0);
      ranges[col] = { min: Math.min(...vals, Infinity), max: Math.max(...vals, 0) };
    });
    return ranges;
  }, [draft, years]);

  const rowTotal = (y) => MONTHS.reduce((s, m) => s + (parseFloat(draft[y]?.[m]) || 0), 0);

  // foreseen monthly targets (per year) from backend statistics
  const forecastLookup = useMemo(() => {
    const m = {};
    (stats.years || []).forEach((s) => { m[s.year] = s.forecast_months || {}; });
    return m;
  }, [stats]);

  const setCell = (y, m, val) => {
    setDraft((d) => ({ ...d, [y]: { ...d[y], [m]: val } }));
    setDirty((dd) => ({ ...dd, [y]: true }));
  };

  const saveYear = async (y) => {
    const months = {};
    MONTHS.forEach((m) => { months[m] = parseFloat(draft[y]?.[m]) || 0; });
    await upsertFinancialYear(y, months);
  };

  const saveAll = async () => {
    const changed = years.filter((y) => dirty[y]);
    if (!changed.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      await Promise.all(changed.map(saveYear));
      toast.success(`Saved ${changed.length} year${changed.length > 1 ? "s" : ""}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const doAddYear = async () => {
    const y = parseInt(newYear, 10);
    if (!y || y < 1900 || y > 2200) { toast.error("Enter a valid year"); return; }
    try {
      await addFinancialYear(y, {});
      toast.success(`Year ${y} added`);
      setAddOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add year");
    }
  };

  const doDeleteYear = async () => {
    try {
      await deleteFinancialYear(deleteYear);
      toast.success(`Year ${deleteYear} deleted`);
      setDeleteYear(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const saveGoal = async () => {
    const g = parseFloat(goalDraft);
    if (isNaN(g)) { toast.error("Enter a valid percentage"); return; }
    setSavingGoal(true);
    try {
      await updateFinancialConfig(g);
      toast.success(`Growth goal set to +${g}%`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update goal");
    } finally {
      setSavingGoal(false);
    }
  };

  if (permLoading) return null;
  if (!isMasterAdmin && !isExposition) {
    return (
      <div className="p-8 pt-24 max-w-md mx-auto text-center" data-testid="financial-access-denied">
        <Lock className="mx-auto text-slate-400 mb-4" size={48} />
        <h1 className="text-xl font-bold text-slate-800">Master Admin only</h1>
        <p className="text-slate-500 mt-2">Financial statistics are restricted to the master administrator.</p>
      </div>
    );
  }

  const chartData = stats.years.map((s) => ({ year: String(s.year), total: s.total }));
  const bestYear = stats.years.reduce((a, b) => (b.total > (a?.total || 0) ? b : a), null);

  return (
    <div className="p-4 md:p-8 pt-20 md:pt-24 space-y-8 max-w-[1400px] mx-auto" data-testid="financial-statistics-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Euro className="text-[#d4af37]" /> Financial Statistics
          </h1>
          <p className="text-slate-500 text-sm mt-1">Monthly &amp; yearly income — totals and year-over-year comparisons.</p>
          {isExposition && <p className="text-amber-600 text-xs mt-1 font-semibold">Demo mode — viewing only.</p>}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { setNewYear(String(new Date().getFullYear())); setAddOpen(true); }} data-testid="add-year-btn">
              <Plus size={16} className="mr-2" /> Add Year
            </Button>
            <Button onClick={saveAll} disabled={saving} className="bg-[#d4af37] hover:bg-[#c19d2f] text-[#0f172a] font-semibold" data-testid="save-all-btn">
              <Save size={16} className="mr-2" /> {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </div>

      {/* Growth goal */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="goal-config">
        <Target size={18} className="text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm font-semibold text-slate-800">Yearly growth goal</p>
          <p className="text-xs text-slate-500">Minimum % above the previous year used for all foreseen targets &amp; the on-track badge.</p>
        </div>
        {canEdit ? (
          <div className="flex items-center gap-2">
            <div className="relative">
              <Input type="number" step="0.1" value={goalDraft} onChange={(e) => setGoalDraft(e.target.value)} className="w-24 pr-6 text-right tabular-nums" data-testid="goal-input" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
            </div>
            <Button onClick={saveGoal} disabled={savingGoal || goalDraft === "" || parseFloat(goalDraft) === goal} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="save-goal-btn">
              {savingGoal ? "Saving..." : "Set goal"}
            </Button>
          </div>
        ) : (
          <span className="text-lg font-bold text-emerald-700 tabular-nums" data-testid="goal-value">+{goal}%</span>
        )}
      </div>

      {/* Editable grid */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="financial-grid">
        <table className="w-full text-sm border-collapse min-w-[1000px]">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="sticky left-0 bg-slate-900 px-3 py-3 text-left font-semibold z-10">Year</th>
              {MONTH_LABELS.map((m) => <th key={m} className="px-1.5 py-3 text-right font-semibold text-xs">{m}</th>)}
              <th className="px-2 py-3 text-right font-semibold bg-slate-800 text-xs">Total</th>
              {canEdit && <th className="px-2 py-3 w-10" />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={15} className="text-center py-10 text-slate-400">Loading…</td></tr>
            ) : years.length === 0 ? (
              <tr><td colSpan={15} className="text-center py-10 text-slate-400">No data yet. Use “Add Year”.</td></tr>
            ) : years.map((y) => {
              const tot = rowTotal(y);
              return (
                <tr key={y} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`financial-row-${y}`}>
                  <td className="sticky left-0 bg-white px-3 py-1.5 font-bold text-slate-800 z-10">{y}</td>
                  {MONTHS.map((m) => {
                    const v = parseFloat(draft[y]?.[m]) || 0;
                    return (
                      <td key={m} className="px-0.5 py-1" style={{ backgroundColor: heat(v, colRange[m].min, colRange[m].max) }}>
                        <input
                          type="number"
                          step="0.01"
                          value={draft[y]?.[m] ?? ""}
                          onChange={(e) => setCell(y, m, e.target.value)}
                          disabled={!canEdit}
                          className="w-full bg-transparent text-right px-1 py-1 rounded text-xs focus:outline-none focus:ring-2 focus:ring-[#d4af37] disabled:cursor-default tabular-nums placeholder:text-emerald-500/70 placeholder:italic"
                          placeholder={forecastLookup[y]?.[m] ? `≈${compact(forecastLookup[y][m])}` : "—"}
                          data-testid={`cell-${y}-${m}`}
                        />
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right font-bold tabular-nums text-xs" style={{ backgroundColor: heat(tot, colRange.total.min, colRange.total.max) }} data-testid={`total-${y}`}>
                    {eur(tot)}
                  </td>
                  {canEdit && (
                    <td className="px-1 text-center">
                      <button onClick={() => setDeleteYear(y)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`delete-year-${y}`}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        Italic <span className="text-emerald-600 font-semibold">≈</span> placeholders in open months are <strong>foreseen targets</strong> — the extra income those months still need so the full year ends at least {goal}% above the previous year.
      </p>

      {/* Yearly totals chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 md:p-6" data-testid="financial-chart">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Yearly income</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 20, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => eur2(v)} />
              <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={bestYear && String(bestYear.year) === d.year ? "#d4af37" : "#0f172a"} />)}
                <LabelList dataKey="total" position="top" formatter={(v) => eur(v)} style={{ fontSize: 11, fill: "#475569" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Add year dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-year-dialog">
          <DialogHeader><DialogTitle>Add a year</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="text-xs text-slate-500">Year</label>
            <Input type="number" value={newYear} onChange={(e) => setNewYear(e.target.value)} className="mt-1" data-testid="new-year-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={doAddYear} className="bg-[#d4af37] hover:bg-[#c19d2f] text-[#0f172a] font-semibold" data-testid="confirm-add-year-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteYear} onOpenChange={(o) => !o && setDeleteYear(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete year {deleteYear}?</AlertDialogTitle>
            <AlertDialogDescription>This removes all monthly income recorded for {deleteYear}. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteYear} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-year-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
